import { useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { updateUserProgress } from "../content/contentSlice";
import { captureException } from "../../utils/errorReporting";

const ANALYTICS_LOG_INTERVAL = 15; // seconds

// assumes it's a video.js player, may not work with other players
export function useVideoJSPlayerAnalytics(id) {
  const dispatch = useDispatch();
  const lastLogTime = useRef(0); // latest point in the video when we logged watched duration
  const currentId = useRef(id); // Add ref to track current id (id changes when user navigates to a different content)

  const recordVideoAnalytics = useCallback(
    async (id, watchedDuration, currentTime) => {
      const payload = { id, watchedDuration, currentTime };
      const response = await axios.post("/api/video-analytics", payload);
      dispatch(updateUserProgress(payload));
      return response.data;
    },
    [dispatch]
  );

  useEffect(() => {
    currentId.current = id;
    lastLogTime.current = 0; // Reset when id changes
  }, [id]);

  const registerVideoAnalyticsHandlers = useCallback(
    player => {
      if (!player) return;
      player.on("timeupdate", async () => {
        if (!player) return;
        const videoId = currentId.current;
        const currentTime = Math.floor(player.currentTime());
        const watchedDuration = Math.max(0, currentTime - lastLogTime.current);

        // skip analytics logging if the video is seeking (user skipped ahead or behind)
        // this prevents for example incorrectly logging 1 hour watched if user skips near the end of a long video
        // player.seeking() is not always reliable, so we make a backup check: "is the video much further ahead than (or behind) the log interval?""
        // some small amount of false positives is ok, e.g. when user skips ahead a few seconds and player.seeking() incorrectly returns false this will log as if they watched it
        const isCurrentTimeFarAhead =
          watchedDuration - (ANALYTICS_LOG_INTERVAL + 5) > ANALYTICS_LOG_INTERVAL;
        const isCurrentTimeBehind = watchedDuration < 1;
        const isSeeking = player.seeking() || isCurrentTimeFarAhead || isCurrentTimeBehind;

        // another reason to skip is because timeupdate is triggered a few times per second during playback
        // so to avoid overwhelming the server, we skip if the watched duration has not reached the log interval
        const hasReachedLogInterval = watchedDuration >= ANALYTICS_LOG_INTERVAL;
        const shouldSkip = isSeeking || !hasReachedLogInterval;
        if (shouldSkip) return;

        try {
          lastLogTime.current = currentTime;
          await recordVideoAnalytics(videoId, watchedDuration, currentTime);
        } catch (error) {
          console.error("Failed to record video analytics.", error);
          captureException(error);
        }
      });

      player.on("seeked", () => {
        if (!player) return;
        // intentionally don't handle seeked events, just reset the last log time
        // because when seeking, it's almost impossible to get the seeked-from time
        // so it's not possible to log the watched duration before the seek
        lastLogTime.current = player.currentTime();
      });
      player.on("pause", () => {
        // intentionally don't handle pause events
        // pause event is triggered in many cases, not just when pausing the video
        // e.g. when the video is ended, when seeking (regardless if video is playing or paused)
        // so for the sake of simplicity it's best to ignore all these cases
      });
      player.on("ended", async () => {
        if (!player) return;
        const videoId = currentId.current;
        const currentTime = Math.floor(player.currentTime());
        const watchedDuration = Math.max(0, currentTime - lastLogTime.current);
        if (watchedDuration < 1) return;
        // log the watched duration and user progress
        try {
          await recordVideoAnalytics(videoId, watchedDuration, currentTime);
        } catch (error) {
          console.error("Failed to record video analytics.", error);
          captureException(error);
        }
      });
    },
    [recordVideoAnalytics]
  );

  const resetVideoAnalytics = useCallback(() => {
    lastLogTime.current = 0;
  }, []);

  return { registerVideoAnalyticsHandlers, resetVideoAnalytics };
}
